import Checkbox from '@orn/shared-web/components/Checkbox/Checkbox';
import DateOfBirthInput, { isValidDateOfBirth } from '@orn/shared-web/components/DateOfBirthInput';
import { HookForm, FormField } from '@orn/shared-web/components/Form';
import Input from '@orn/shared-web/components/Input';
import Select from '@orn/shared-web/components/Select/Select';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { RELATIONSHIP_OPTIONS } from '../../../lib/dependants/constants';
import { OnboardingTitle } from '../../OnboardingLayout';
import { useOnboardingContext } from '../OnboardingContext';

import OnboardingStepActions from './OnboardingStepActions';

import type { CaregiverRelationshipKind } from '../../../__generated__/globalTypes';
import type { IDependantFormValues } from '../dependants/types';
import type { IOnboardingStepProps } from '../types';

const getStepTitle = (index: number, total: number): string => {
  if (total === 1) {
    return 'Tell us about your loved one';
  }
  return index === 0 ? 'Tell us about the first person' : 'Tell us about the second person';
};

const areValuesComplete = (values: IDependantFormValues): boolean =>
  values.relationship !== null &&
  values.firstName.trim() !== '' &&
  values.lastName.trim() !== '' &&
  values.dateOfBirth !== null &&
  isValidDateOfBirth(values.dateOfBirth);

interface IInnerFormProps {
  title: string;
  defaultValues: IDependantFormValues;
  submitLabel: string;
  onBack?: () => void;
  onSubmit: (values: IDependantFormValues) => void;
}

/**
 * Renders one dependant slot's form. The parent re-keys this per slot so RHF
 * re-reads its `defaultValues` cleanly when the pager moves between slots. The
 * submit button stays disabled until the required fields are valid.
 */
const DependantForm = ({ title, defaultValues, submitLabel, onBack, onSubmit }: IInnerFormProps) => {
  const form = useForm<IDependantFormValues>({
    defaultValues,
    mode: 'onSubmit',
  });

  // Live-validate to gate the submit button — `watch()` re-renders on every
  // field change so the button enables the moment the required fields are valid.
  const isComplete = areValuesComplete(form.watch());

  const handleSubmit = React.useCallback(
    (values: IDependantFormValues) => {
      if (!values.relationship) {
        form.setError('relationship', { message: 'Relationship is required.' });
        return;
      }
      if (values.firstName.trim() === '') {
        form.setError('firstName', { message: 'First name is required.' });
        return;
      }
      if (values.lastName.trim() === '') {
        form.setError('lastName', { message: 'Last name is required.' });
        return;
      }
      if (!values.dateOfBirth || !isValidDateOfBirth(values.dateOfBirth)) {
        form.setError('dateOfBirth', { message: 'Please enter a valid date of birth.' });
        return;
      }
      onSubmit({
        ...values,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        preferredName: values.preferredName.trim(),
      });
    },
    [form, onSubmit]
  );

  return (
    <>
      <OnboardingTitle title={title} onBack={onBack} />

      <HookForm form={form} onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="flex w-full flex-col gap-4">
          <FormField control={form.control} name="relationship">
            {({ field, fieldState }) => (
              <div className="flex w-full flex-col gap-2">
                <Select
                  label="Relationship"
                  placeholder="Select a relationship"
                  size="md"
                  items={RELATIONSHIP_OPTIONS}
                  selectedKey={field.value}
                  onSelectionChange={(key) => field.onChange(key as CaregiverRelationshipKind | null)}
                  hint={fieldState.error?.message}
                  isInvalid={Boolean(fieldState.error)}
                  isRequired
                >
                  {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                </Select>
                <div className="flex gap-6">
                  <FormField control={form.control} name="isStep">
                    {({ field: stepField }) => (
                      <Checkbox isSelected={stepField.value} onChange={stepField.onChange} size="md" label="Step" />
                    )}
                  </FormField>
                  <FormField control={form.control} name="isInLaw">
                    {({ field: lawField }) => (
                      <Checkbox isSelected={lawField.value} onChange={lawField.onChange} size="md" label="In law" />
                    )}
                  </FormField>
                </div>
              </div>
            )}
          </FormField>

          <div className="flex gap-3">
            <FormField control={form.control} name="firstName">
              {({ field, fieldState }) => (
                <Input
                  {...field}
                  label="First name"
                  placeholder="Jane"
                  size="md"
                  isRequired
                  isInvalid={Boolean(fieldState.error)}
                  hint={fieldState.error?.message}
                  autoComplete="off"
                />
              )}
            </FormField>
            <FormField control={form.control} name="lastName">
              {({ field, fieldState }) => (
                <Input
                  {...field}
                  label="Last name"
                  placeholder="Smith"
                  size="md"
                  isRequired
                  isInvalid={Boolean(fieldState.error)}
                  hint={fieldState.error?.message}
                  autoComplete="off"
                />
              )}
            </FormField>
          </div>

          <FormField control={form.control} name="preferredName">
            {({ field }) => (
              <Input
                {...field}
                label="Preferred name (optional)"
                description="We'll use this name to refer to them across Mantle"
                placeholder="e.g. Janey"
                size="md"
                autoComplete="off"
              />
            )}
          </FormField>

          <FormField control={form.control} name="dateOfBirth">
            {({ field, fieldState }) => (
              <DateOfBirthInput
                value={field.value}
                onChange={field.onChange}
                isRequired
                isInvalid={Boolean(fieldState.error)}
                hint={fieldState.error?.message}
              />
            )}
          </FormField>

          <OnboardingStepActions continueType="submit" continueLabel={submitLabel} isContinueDisabled={!isComplete} />
        </div>
      </HookForm>
    </>
  );
};

/**
 * Per-dependant entry form for the loved-ones flow — a linear pager over the
 * slots, showing one at a time. There's no separate "edit" mode: editing a
 * dependant just drops you back into the flow at that slot and continues
 * forward (e.g. Edit person 1 → person 1 → person 2 → review).
 *
 * Opens on the slot that review's Edit-on-card targeted (`editingDependantIndex`),
 * else the first slot still needing input, else slot 0 — so backing in from a
 * completed review retraverses from the first person, while bumping the count
 * up lands on the freshly-added empty slot. Continue advances to the next slot
 * or, past the last, to review; Back pages to the previous slot or, from the
 * first, out to the count step.
 *
 * Persistence is intentionally deferred to `DependantReviewStep` — this step
 * is a pure form, agnostic of whether each slot will create a new row or
 * update an existing one.
 *
 * Figma: Mantle - UX/UI, node 4157:68505 ("Onboarding Step 08 - Loved One Entry").
 */
const DependantEntryStep = ({ onNext, onBack }: IOnboardingStepProps) => {
  const { dependantCount, pendingDependants, setPendingDependants, editingDependantIndex, setEditingDependantIndex } =
    useOnboardingContext();

  const effectiveCount: 1 | 2 = dependantCount ?? 1;
  const slots = pendingDependants.slice(0, effectiveCount);

  // Opening slot, computed once at mount (the orchestrator remounts this step
  // on every screen change): the Edit-on-card target if there is one, else the
  // first slot still needing input, else slot 0. In the real flow pending is
  // never partial at mount — forward-from-count is all-empty or (resumed)
  // all-complete, back-from-review is all-complete — so this lands on slot 0 and
  // the user retraverses; the first-incomplete case only bites when the count
  // just grew (1 → 2), where opening on the new empty slot is what we want.
  const [slotIndex, setSlotIndex] = React.useState(() => {
    if (editingDependantIndex !== null && editingDependantIndex < slots.length) {
      return editingDependantIndex;
    }
    const firstIncomplete = slots.findIndex((slot) => !areValuesComplete(slot.values));
    return firstIncomplete === -1 ? 0 : firstIncomplete;
  });

  // `editingDependantIndex` is a one-shot opening hint — consume it so a later
  // mount (e.g. backing in from review) opens on slot 0, not the edited one.
  // The initial position was already captured above.
  React.useEffect(() => {
    if (editingDependantIndex !== null) {
      setEditingDependantIndex(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (slots.length === 0) {
    return null;
  }

  const handleContinue = (values: IDependantFormValues) => {
    setPendingDependants((prev) => prev.map((slot, i) => (i === slotIndex ? { ...slot, values } : slot)));
    if (slotIndex < effectiveCount - 1) {
      setSlotIndex(slotIndex + 1);
    } else {
      onNext();
    }
  };
  // Slot 0 back exits to the count step; a later slot pages to the previous one.
  const handleBack = slotIndex === 0 ? onBack : () => setSlotIndex(slotIndex - 1);

  return (
    <DependantForm
      key={`slot-${slotIndex}`}
      title={getStepTitle(slotIndex, effectiveCount)}
      defaultValues={slots[slotIndex].values}
      submitLabel="Continue"
      onBack={handleBack}
      onSubmit={handleContinue}
    />
  );
};

DependantEntryStep.displayName = 'DependantEntryStep';

export default DependantEntryStep;
